import React from 'react';
import { arrayOf, bool, shape, string, number } from 'prop-types';

import { Image } from 'nordic/image';
import classnames from 'classnames';

import Price from '../../../card/price';
import Reviews from '../../../../components-v2/reviews';
import ItemHighlight from '../../../card/highlight/highlight-label';
import IconBuilder from '../icon-builder';
import RebatesPill from '../rebates-pill';
import { setAddToCartItemCpgViewMode } from '../../../context/search';
import { CARD_DATA_GROUPS } from '../../../../components-v2/layout/constants';
import Group from '../../../card/group/group';
import AddToCart from '../../../card/add-to-cart';
import { VERTICAL_TYPE } from '../../../../constants';

const renderAddToCartComponent = (item) => {
  if (item.cpg) {
    return (
      <Group noSeparator name={CARD_DATA_GROUPS.ADD_TO_CART}>
        <AddToCart
          {...item.cpg}
          isCpg={item.vertical === VERTICAL_TYPE.CPG}
          itemId={item.id}
          productId={item?.product?.id}
          category={item.category_id}
          availableQuantity={item.available_quantity}
          minimumQuantity={item.minimum_quantity}
          labelMinQuantity={item.label_min_quantity}
          inventoryId={item.inventory_id}
          label={item.title}
          groupBy={item.group_by}
          threshold={item.threshold}
          isAd={item.is_ad}
          permalink={item.permalink}
          viewMode={item.cpg.viewMode}
        />
      </Group>
    );
  }

  return null;
};

const pillsBuilder = (pill, isPadIntervention) => {
  const pillText = pill.text.replace('{full_icon}', '');

  if (pill.rebates) {
    return <RebatesPill pill={pill} />;
  }

  // se usa en intervenciones

  return (
    <div aria-label={pill.accessibility_text} className="content-label" key={pillText}>
      <span
        className={classnames({
          'icon-label': pillText.length > 0,
          'free-shipping': !pill?.values,
          'pads-intervention-label': isPadIntervention,
        })}
      >
        {pillText}
      </span>
      {pill?.values && <div className="icon-pill">{IconBuilder[pill.values[0].id]}</div>}
    </div>
  );
};

const createAddToCartItem = ({ itemId, cardIndex, add_to_cart_item }) => {
  const { cpg, ...obj } = add_to_cart_item || {};

  if (add_to_cart_item?.cpg) {
    const viewMode = 'intervention';

    try {
      setAddToCartItemCpgViewMode({
        itemId,
        cardIndex,
        viewMode,
      });
    } catch (e) {
      // ignore
    }

    obj.cpg = {
      ...cpg,
      viewMode,
    };
  }

  return obj;
};
const formatPrice = (price) => {
  const { value, decimal_value } = price;
  const hasDecimalValue = decimal_value && parseInt(decimal_value, 10) !== 0;

  return Number(`${value}${value < 1000 && hasDecimalValue ? `.${decimal_value}` : ''}`);
};

const CardItem = ({
  add_to_cart_item,
  discount,
  item,
  name,
  permalink,
  picture,
  pills,
  isPadIntervention,
  price,
  reviews,
  superIntervention,
  hasExtraSpacing,
  itemId,
  cardIndex,
  float_highlight,
}) => {
  const formattedPrice = React.useMemo(() => formatPrice(price), [price]);

  return (
    <div
      aria-hidden="true"
      data-testid={picture?.id}
      className={classnames('card-item card-item--dynamic-width', {
        'card-item--super': superIntervention && item?.cpg,
        'button-with-label': hasExtraSpacing,
        'button-without-label': !hasExtraSpacing,
      })}
    >
      {float_highlight && <ItemHighlight {...float_highlight} />}
      <div className="card-item-img">
        <Image
          src={`https://http2.mlstatic.com/S_Q_NP_2X_${picture?.id}-V.webp`}
          alt={name.text}
          lazyload="on"
          className="image"
        />
      </div>
      <div className="card-content">
        {reviews && (
          <div className="card-content__reviews-content" data-testid="card-item-reviews">
            <Reviews
              rating={reviews.rating_average}
              stars={reviews.rating_stars}
              formattedTotal={reviews.formatted_total}
              formattedAverage={reviews.rating_average_formatted}
              accesibilityText={reviews?.accessibility_text}
            />
          </div>
        )}
        <a href={permalink} className="card-content__title">
          {name.text}
        </a>
        <Price
          amount={formattedPrice}
          currency_id={price.currency_id}
          original_price={price?.original_price}
          discount_label={discount}
          showOriginalValue={price.original_price > 0}
          bulksale={price?.bulksale}
          size="small"
          className={`card-content__${superIntervention ? 'super-description' : 'item-description'}`}
        />
        {pills && (
          <div className="card-content__pills-content">
            {pills?.map((pill) => pillsBuilder(pill, isPadIntervention))}
          </div>
        )}
      </div>
      {add_to_cart_item?.cpg && renderAddToCartComponent(createAddToCartItem({ itemId, cardIndex, add_to_cart_item }))}
    </div>
  );
};

CardItem.propTypes = {
  add_to_cart_item: shape({
    accepts_mercado_pago: bool,
    available_quantity: number,
    bookmarked: bool,
    cart_quantity: number,
    category_id: string,
    cpg: shape({
      action: shape({
        label: shape({
          text: string,
        }),
      }),
      cart_request_delay: number,
      cart_request_initial_delay: number,
      id: string,
      label_count: shape({
        quantity: number,
        text: string,
        values: arrayOf(
          shape({
            color: string,
            font_weight: string,
            key: string,
            text: string,
            type: string,
          }),
        ),
      }),
      quantity: number,
      should_call_suggestions: bool,
      tracks: shape({
        melidata_track: shape({
          event_data: shape({
            context: string,
            flow_type: string,
            items: arrayOf(
              shape({
                item: shape({
                  category_id: string,
                  id: string,
                }),
                currency_id: string,
                quantity: number,
                unit_price: string,
              }),
            ),
          }),
          path: string,
          type: string,
        }),
        path: shape({
          add: string,
          error: string,
          remove: string,
        }),
      }),
      type: string,
    }),
    group_by: string,
    has_color_variations: bool,
    id: string,
    inventory_id: string,
    minimum_quantity: number,
    tags: arrayOf(string),
    threshold: number,
    variation_ids: arrayOf(string),
    variations_info: arrayOf(string),
  }),
  cardIndex: number,
  discount: shape({
    text: string,
  }),
  float_highlight: shape({
    background: string,
    className: string,
    id: string,
    label: shape({
      color: string,
      text: string,
    }),
    type: string,
  }),
  hasExtraSpacing: bool,
  isPadIntervention: bool,
  item: shape({
    category_id: string,
    cpg: shape({}),
    id: string,
    inventory_id: string,
    product: shape({
      id: string,
    }),
    title: string,
  }),
  itemId: string,
  name: shape({
    text: string,
  }),
  permalink: string,
  picture: shape({
    id: string,
  }),
  pills: arrayOf(
    shape({
      accessibility_text: string,
      rebates: bool,
      text: string,
      values: arrayOf(
        shape({
          id: string,
        }),
      ),
    }),
  ),
  price: shape({
    currency_symbol: string,
    decimal_value: string,
    value: string,
  }),
  reviews: shape({
    accessibility_text: string,
    formatted_total: string,
    rating_average: number,
    rating_average_formatted: string,
    rating_stars: shape({
      color: string,
      size: number,
      value: number,
    }),
  }),
  superIntervention: bool,
};

export default CardItem;
